import type { PayloadAction } from '@reduxjs/toolkit'
import { createAction, createSlice } from '@reduxjs/toolkit'
import type { ComparativeTableV2Dto, ParticipantWinnerDto, TenderDetailedInfo } from '@/api/kubik/tender'
import type { RootState } from '@/redux/interfaces'

export interface ITenderState {
  tender: TenderDetailedInfo | null
  isTenderLoading: boolean
  error: string | null
  comparativeTable: ComparativeTableV2Dto | null
  participantInfo: ParticipantWinnerDto | null
  isComparativeTableLoading: boolean
  isParticipantInfoLoading: boolean
}

const initialState: ITenderState = {
  tender: null,
  isTenderLoading: false,
  error: null,
  comparativeTable: null,
  participantInfo: null,
  isComparativeTableLoading: false,
  isParticipantInfoLoading: false,
}

export const fetchTender = createAction<number>('tender/fetchTender')
export const fetchComparativeTable = createAction<number>('tender/fetchComparativeTable')
export const fetchParticipantInfo = createAction<number>('tender/fetchParticipantInfo')

const tenderSlice = createSlice({
  name: 'tender',
  initialState,
  reducers: {
    setTender: (state, action: PayloadAction<TenderDetailedInfo>) => {
      state.tender = action.payload
      state.error = null
    },
    setTenderLoading: (state, action: PayloadAction<boolean>) => {
      state.isTenderLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
    },
    clearTender: state => {
      state.tender = null
      state.error = null
    },
    setComparativeTable: (state, action: PayloadAction<ComparativeTableV2Dto | null>) => {
      state.comparativeTable = action.payload
    },
    setComparativeTableLoading: (state, action: PayloadAction<boolean>) => {
      state.isComparativeTableLoading = action.payload
    },
    clearComparativeTable: state => {
      state.comparativeTable = null
    },
    setParticipantInfo: (state, action: PayloadAction<ParticipantWinnerDto | null>) => {
      state.participantInfo = action.payload
    },
    setParticipantInfoLoading: (state, action: PayloadAction<boolean>) => {
      state.isParticipantInfoLoading = action.payload
    },
    clearParticipantInfo: state => {
      state.participantInfo = null
    },
    resetTender: () => {
      return initialState
    },
  },
})

export const {
  setTender,
  setTenderLoading,
  setError,
  clearTender,
  setComparativeTable,
  setComparativeTableLoading,
  setParticipantInfo,
  setParticipantInfoLoading,
  clearParticipantInfo,
  clearComparativeTable,
  resetTender,
} = tenderSlice.actions

export const selectTender = (state: RootState) => state.tender.tender
export const selectTenderLoading = (state: RootState) => state.tender.isTenderLoading
export const selectTenderError = (state: RootState) => state.tender.error
export const selectComparativeTable = (state: RootState) => state.tender.comparativeTable
export const selectComparativeTableLoading = (state: RootState) => state.tender.isComparativeTableLoading
export const selectParticipantInfo = (state: RootState) => state.tender.participantInfo
export const selectParticipantInfoLoading = (state: RootState) => state.tender.isParticipantInfoLoading

export default tenderSlice.reducer
